import React, {useState, useCallback} from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stack,
    Box,
    Typography,
    Alert,
    IconButton,
    Tooltip
} from "@mui/material";
import {IconCheck, IconCopy, IconX} from "@tabler/icons-react";
import Editor from "@monaco-editor/react";
import JSONSchemaBuilder from "./JSONSchemaBuilder";

export default function SchemaBuilderModal({open, handleClose, onSuccess, text}) {
    const [pendingSchema, setPendingSchema] = useState(null);
    const [warnings, setWarnings] = useState([]);
    const [copied, setCopied] = useState(false);
    const handleCopy = (text) => {
        navigator.clipboard.writeText(text).catch(() => {
        });
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const handleBuilderChange = useCallback((schema, schemaWarnings) => {
        setPendingSchema(schema);
        setWarnings(schemaWarnings || []);
    }, []);

    const handleSave = () => {
        if (!pendingSchema) return;
        onSuccess?.(JSON.stringify(pendingSchema, null, 2));
        handleClose?.();
    };

    const hasBlockingWarnings = warnings.length > 0;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, py: 2.5, borderBottom: "1px solid", borderColor: "divider" }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Stack spacing={0.25}>
                        <Typography sx={{ fontWeight: 700, fontSize: 16, lineHeight: 1.2 }}>JSON Schema builder</Typography>
                        <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>Define the fields the model should extract</Typography>
                    </Stack>
                </Stack>
                <IconButton onClick={handleClose} size="small" sx={{ color: "text.secondary", bgcolor: "action.hover", "&:hover": { bgcolor: "action.selected" } }}>
                    <IconX size={16} />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {hasBlockingWarnings && (
                    <Alert severity="warning" sx={{mb: 2}}>
                        <Stack spacing={0.5}>
                            {warnings.map((w, i) => (
                                <Typography key={i} variant="caption" component="div">
                                    {w}
                                </Typography>
                            ))}
                        </Stack>
                    </Alert>
                )}

                <Stack direction={{xs: "column", md: "row"}} spacing={2}>
                    <Box sx={{flex: 1, minWidth: 0, maxHeight: 560, overflow: "auto"}}>
                        <JSONSchemaBuilder
                            key={open ? "open" : "closed"}
                            initialSchema={text}
                            showPreview={false}
                            onChange={handleBuilderChange}
                        />
                    </Box>

                    <Box sx={{flex: 1, minWidth: 0}}>
                        <Stack direction="row" gap={1} justifyContent={"space-between"}>
                            <Typography variant="subtitle2" sx={{mb: 1}}>
                            Schema preview
                        </Typography>
                            <Tooltip title={"Copy JSON schema"}>
                                <IconButton onClick={() => handleCopy(pendingSchema ? JSON.stringify(pendingSchema, null, 2) : text)}>
                                    {copied ? <IconCheck color={"green"} size={15}/> :
                                        <IconCopy size={15}/>}
                                </IconButton>
                            </Tooltip></Stack>

                        <Editor
                            fontSize
                            height="520px"
                            defaultLanguage="json"
                            value={pendingSchema ? JSON.stringify(pendingSchema, null, 2) : text}
                            options={{readOnly: true, minimap: {enabled: false}, fontSize: 12}}
                        />
                    </Box>
                </Stack>
            </DialogContent>

            <DialogActions sx={{ padding: "16px 20px", gap: 1 }}>
                <Button
                    variant={"ghost"}
                    sx={{ textTransform: "capitalize" }}
                    onClick={handleClose}
                >
                    Cancel
                </Button>
                <Button
                    variant={"modal"}
                    sx={{ textTransform: "capitalize" }}
                    disabled={hasBlockingWarnings}
                    onClick={handleSave}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}